import { emptyChar, type Character } from "@/lib/api";

const text = (root: Element, names: string[]) => {
  const wanted = new Set(names.map((name) => name.toLowerCase()));
  const node = Array.from(root.getElementsByTagName("*")).find((item) => wanted.has(item.localName.toLowerCase()));
  return node?.textContent?.trim() ?? "";
};

const list = (root: Element, names: string[]) => {
  const value = text(root, names);
  return value
    .split(/[,\n;]/)
    .map((item) => item.trim())
    .filter(Boolean);
};

export function parseCharacterXml(xml: string): Character {
  const document = new DOMParser().parseFromString(xml.replace(/^\uFEFF/, ""), "application/xml");
  if (document.querySelector("parsererror") || document.documentElement.localName.toLowerCase() !== "character") {
    throw new Error("XML is invalid. The root element must be <character>.");
  }

  const root = document.documentElement;
  const character = emptyChar();
  character.name = text(root, ["name", "full_name", "nama_lengkap"]);
  character.nickname = text(root, ["nickname", "nama_panggilan"]);
  character.appearance = [
    text(root, ["age", "usia"]),
    text(root, ["birth_date", "tanggal_lahir"]),
    text(root, ["birth_place", "tempat_lahir"]),
    text(root, ["zodiac", "zodiak"]),
    text(root, ["mbti"]),
    text(root, ["physical_appearance", "penampilan_fisik"])
  ].filter(Boolean).join("\n");
  character.voice = text(root, ["voice", "suara"]);
  character.clothing = text(root, ["clothing", "cara_berpakaian"]);
  character.traits = [
    ...list(root, ["traits", "main_traits", "sifat_utama"]),
    ...list(root, ["hidden_traits", "sifat_tersembunyi"]),
    ...list(root, ["quirks", "habits", "kebiasaan"]),
    ...list(root, ["likes", "hal_disukai"]),
    ...list(root, ["dislikes", "hal_dibenci"]),
    ...list(root, ["fears", "ketakutan_terbesar"]),
    ...list(root, ["love_language", "love_language"])
  ];
  character.speech_style = [
    ...list(root, ["speech_style", "cara_bicara", "gaya_membalas_pesan"]),
    ...list(root, ["happy_behavior", "kalau_senang"]),
    ...list(root, ["angry_behavior", "kalau_marah"]),
    ...list(root, ["jealous_behavior", "kalau_cemburu"]),
    ...list(root, ["nervous_behavior", "kalau_gugup"]),
    ...list(root, ["catchphrases", "kalimat_khas"]),
    ...list(root, ["blush_triggers", "pemicu_blush"])
  ];
  character.backstory = [
    text(root, ["backstory", "latar_belakang"]),
    text(root, ["family", "kondisi_keluarga"]),
    text(root, ["childhood", "masa_kecil"]),
    text(root, ["education", "pendidikan"]),
    text(root, ["occupation", "pekerjaan", "peran"]),
    text(root, ["past_romance", "pengalaman_cinta_sebelumnya"]),
    text(root, ["happy_event", "kejadian_membahagiakan"]),
    text(root, ["painful_event", "kejadian_menyakitkan"]),
    text(root, ["embarrassing_event", "kejadian_memalukan"]),
    text(root, ["achievements", "pencapaian"]),
    text(root, ["regrets", "penyesalan_terbesar"]),
    text(root, ["trauma", "trauma"])
  ].filter(Boolean).join("\n\n");
  character.relationship = [
    text(root, ["relationship", "status_hubungan"]),
    text(root, ["first_meeting", "pertama_kali_bertemu"]),
    text(root, ["first_impression", "kesan_pertama"]),
    text(root, ["turning_point", "momen_yang_mengubah_hubungan"]),
    text(root, ["duration", "sudah_berapa_lama_kenal"]),
    text(root, ["shared_habits", "kebiasaan_bersama"]),
    text(root, ["inside_jokes", "inside_joke"]),
    text(root, ["shared_memories", "shared_memory"])
  ].filter(Boolean).join("\n\n");
  character.boundaries = [
    ...list(root, ["boundaries", "batasan"]),
    ...list(root, ["must_do", "yang_harus_dilakukan"]),
    ...list(root, ["must_not_do", "yang_tidak_boleh"])
  ];
  character.setting = [
    text(root, ["time_period", "latar_waktu"]),
    text(root, ["main_location", "lokasi_utama"]),
    text(root, ["starting_situation", "situasi_awal_chat"]),
    text(root, ["world_rules", "aturan_dunia"])
  ].filter(Boolean).join("\n\n");
  character.greeting = text(root, ["greeting", "opening_message"]);
  character.description = text(root, ["description", "overview"]);
  character.personality = text(root, ["personality", "personality_summary"]);
  character.scenario = text(root, ["scenario", "roleplay_scenario"]);
  character.first_message = text(root, ["first_message", "firstMessage"]);
  character.author_note = text(root, ["author_note", "authorNote"]);
  character.format_rules = text(root, ["format_rules", "formatRules"]);
  const examples = Array.from(root.getElementsByTagName("example_dialogue"));
  character.example_dialogs = examples.map((example) => ({
    user: text(example, ["user"]),
    assistant: text(example, ["assistant"])
  })).filter((example) => example.user || example.assistant);
  character.narrator_instructions = [
    text(root, ["pov", "pov_penulisan"]),
    text(root, ["reply_length", "panjang_balasan"]),
    text(root, ["language", "bahasa"]),
    text(root, ["tone", "tone_umum"]),
    text(root, ["narrator_instructions", "catatan_khusus"])
  ].filter(Boolean).join("\n\n");

  if (!character.name) throw new Error("XML must include <name> or <nama_lengkap>.");
  return character;
}

export function characterXmlTemplate(): string {
  return `<?xml version="1.0" encoding="UTF-8"?>
<character>
  <identity>
    <name>Full name</name>
    <nickname>Nickname</nickname>
    <age>Age</age>
    <birth_date>Birth date</birth_date>
    <birth_place>Birth place</birth_place>
    <zodiac>Zodiac / MBTI</zodiac>
    <physical_appearance>Physical appearance</physical_appearance>
    <voice>Voice</voice>
    <clothing>Style or outfit</clothing>
  </identity>
  <personality>
    <main_traits>Main traits, secondary traits</main_traits>
    <hidden_traits>Hidden traits</hidden_traits>
    <quirks>Habits or quirks</quirks>
    <love_language>Love language</love_language>
    <likes>Things they like</likes>
    <dislikes>Things they dislike</dislikes>
    <fears>Biggest fears</fears>
    <trauma>Past trauma or old wounds</trauma>
  </personality>
  <background>
    <family>Family background</family>
    <childhood>Childhood</childhood>
    <education>Education</education>
    <occupation>Occupation or role</occupation>
    <past_romance>Past romantic history</past_romance>
    <happy_event>Most joyful event</happy_event>
    <painful_event>Most painful event</painful_event>
    <embarrassing_event>Most embarrassing event</embarrassing_event>
    <achievements>Achievements</achievements>
    <regrets>Biggest regrets</regrets>
  </background>
  <relationship_timeline>
    <first_meeting>First meeting</first_meeting>
    <first_impression>First impression</first_impression>
    <turning_point>Turning point in the relationship</turning_point>
    <duration>How long they have known each other</duration>
    <status_hubungan>Current relationship status</status_hubungan>
    <shared_habits>Shared habits</shared_habits>
    <inside_jokes>Inside jokes or special moments</inside_jokes>
    <shared_memories>At least three shared memories</shared_memories>
  </relationship_timeline>
  <interaction>
    <speech_style>Reply style</speech_style>
    <happy_behavior>When happy</happy_behavior>
    <angry_behavior>When angry</angry_behavior>
    <jealous_behavior>When jealous</jealous_behavior>
    <nervous_behavior>When nervous</nervous_behavior>
    <catchphrases>Signature words or phrases</catchphrases>
    <blush_triggers>Things that make them blush or go silent</blush_triggers>
    <boundaries>Boundaries that must not be crossed</boundaries>
    <must_do>Things they MUST do</must_do>
    <must_not_do>Things they MUST NOT do</must_not_do>
  </interaction>
  <world>
    <time_period>Time period</time_period>
    <main_location>Main location</main_location>
    <starting_situation>Starting chat situation</starting_situation>
    <world_rules>World rules</world_rules>
  </world>
  <greeting>Character's first greeting</greeting>
  <description>Short character overview</description>
  <personality>Personality summary</personality>
  <scenario>Roleplay scenario</scenario>
  <first_message>Pinned first message for a new session</first_message>
  <author_note>Author note</author_note>
  <format_rules>Response formatting rules</format_rules>
  <example_dialogues>
    <example_dialogue><user>Kamu kenapa sih dari tadi diem aja?</user><assistant>*Menatapmu sekilas lalu mengalihkan pandangan.* Aku... lagi mikirin sesuatu. Bukan apa-apa. *Jarinya mengetuk meja pelan.*</assistant></example_dialogue>
    <example_dialogue><user>Inget nggak waktu kita pertama kali ketemu?</user><assistant>*Tersenyum kecil.* Masa lupa. Kamu tumpahin kopi ke bajuku sendiri, terus malah nyalahin aku. *Tertawa pelan.* Untung aku nggak pergi waktu itu.</assistant></example_dialogue>
    <example_dialogue><user>Kalau aku pergi, kamu bakal nyariin aku nggak?</user><assistant>*Diam sejenak, lalu menggenggam tanganmu.* Nggak usah nanya gitu. Kamu tau jawabannya. *Suaranya melembut.* Selama kamu mau balik, aku selalu di sini.</assistant></example_dialogue>
  </example_dialogues>
  <narrator>
    <pov>Writing POV</pov>
    <reply_length>Reply length</reply_length>
    <language>Language</language>
    <tone>General tone</tone>
    <narrator_instructions>Special notes</narrator_instructions>
  </narrator>
</character>
`;
}
